import { useQueryClient } from '@tanstack/react-query';
import { MessageSquarePlus, PanelLeft, Pencil } from 'lucide-react-native';
import { useCallback, useMemo, useState } from 'react';
import { KeyboardAvoidingView, Platform, Pressable, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Box, Text } from '../../src/components/primitives';
import { useAuth } from '../../src/contexts/AuthContext';
import { useEdgeToEdge } from '../../src/contexts/LayoutChromeContext';
import { useTranslation } from '../../src/contexts/TranslationContext';
import { ConversationDrawer } from '../../src/features/chat/components/ConversationDrawer';
import { useChatStream } from '../../src/features/chat/hooks/useChatStream';
import {
  CHAT_KEYS,
  useConversationMessages,
  useConversations,
  useCreateConversation,
} from '../../src/features/chat/hooks/useConversations';
import type { GetMessagesResponse } from '../../src/features/chat/types';
import { AgentStatusPanel } from '../../src/features/prospect-search/components/AgentStatusPanel';
import { ChatInput } from '../../src/features/prospect-search/components/ChatInput';
import { ChatMessageList } from '../../src/features/prospect-search/components/ChatMessageList';
import { SearchResultsPanel } from '../../src/features/prospect-search/components/SearchResultsPanel';
import {
  useMessages as useProspectSearchMessages,
  useSession as useProspectSearchSession,
} from '../../src/features/prospect-search/hooks/useProspectSearch';
import { useProspectSearchStream } from '../../src/features/prospect-search/hooks/useProspectSearchStream';
import type { ProspectSearchMessage } from '../../src/features/prospect-search/types';
import { redactUserFacingText } from '../../src/features/prospect-search/utils/safety';
import { colors } from '../../src/theme/colors';

// Resting gutter under the composer. The MobileTabBar below this screen
// already consumes the safe-area bottom inset (home indicator), so we
// only need a small breathing margin here — adding insets.bottom on top
// of the tab bar's own inset double-counts and pushes the input far
// above the tab bar on iPhones with a home indicator.
const COMPOSER_BOTTOM_GUTTER = 8;

export default function ChatScreen() {
  const { currentOrganization, getAccessToken } = useAuth();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const queryClient = useQueryClient();
  const organizationId = currentOrganization?.id;

  useEdgeToEdge();

  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [activeProspectSessionRef, setActiveProspectSessionRef] = useState<string | undefined>();
  // Edit-and-resend (FLOSEAI-553): composerDraft prefills the input with the
  // last user message; isEditingLast routes the next send through editAndResend.
  const [composerDraft, setComposerDraft] = useState<string | null>(null);
  const [isEditingLast, setIsEditingLast] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Eagerly load existing conversations (used by the drawer).
  const { data: conversations } = useConversations(organizationId);
  const createMutation = useCreateConversation(organizationId);

  // Server-backed history. The query cache is now the source of truth for
  // chat messages — when the user navigates away and back, the messages
  // stay because we refetch (and optimistic writes survive in cache too).
  const { data: serverMessages } = useConversationMessages(organizationId, activeConversationId);
  const visibleProspectSessionRef = activeProspectSessionRef;

  const appendToCache = useCallback(
    (
      conversationId: string,
      message: { role: 'user' | 'assistant'; content: string; createdAt: string },
    ) => {
      if (!organizationId) return;
      queryClient.setQueryData<GetMessagesResponse>(
        CHAT_KEYS.messages(organizationId, conversationId),
        (prev) => ({
          messages: [
            ...(prev?.messages ?? []),
            {
              id: `${message.role}-${Date.now()}`,
              conversationId,
              organizationId,
              role: message.role,
              content: message.content,
              metadata: {},
              createdAt: message.createdAt,
            },
          ],
          hasMore: prev?.hasMore ?? false,
        }),
      );
    },
    [organizationId, queryClient],
  );

  const {
    isStreaming,
    currentText,
    status,
    error: streamError,
    errorKind: streamErrorKind,
    pendingAction,
    isResolvingPendingAction,
    approvePendingAction,
    rejectPendingAction,
    sendMessage: sendOrchestratorMessage,
    cancel: cancelOrchestratorRun,
    editAndResend: editAndResendOrchestratorMessage,
    reset: resetStream,
  } = useChatStream({
    conversationId: activeConversationId,
    organizationId,
    getAccessToken,
    onMessageStop: ({ message }) => {
      if (!activeConversationId) return;
      appendToCache(activeConversationId, {
        role: 'assistant',
        content: message,
        createdAt: new Date().toISOString(),
      });
    },
    onRunCancelled: ({ executedActions }) => {
      if (!activeConversationId) return;
      const content =
        executedActions.length > 0
          ? t('missionControl.chat.cancelledWithActions', {
              actions: executedActions.map((action) => action.label).join(', '),
            })
          : t('missionControl.chat.cancelledNoActions');
      appendToCache(activeConversationId, {
        role: 'assistant',
        content,
        createdAt: new Date().toISOString(),
      });
    },
    onProspectSearchInvoked: (sessionRef) => {
      setActiveProspectSessionRef(sessionRef);
    },
  });

  const { agents } = useProspectSearchStream({
    sessionRef: visibleProspectSessionRef,
    organizationId,
    getAccessToken,
  });
  const { data: prospectSession } = useProspectSearchSession(
    visibleProspectSessionRef,
    organizationId,
  );
  const { data: prospectMessages } = useProspectSearchMessages(
    organizationId,
    visibleProspectSessionRef,
  );

  const ensureConversation = useCallback(async (): Promise<string | null> => {
    if (activeConversationId) return activeConversationId;
    if (!organizationId) return null;
    try {
      const created = await createMutation.mutateAsync({ title: 'New chat' });
      setActiveConversationId(created.id);
      return created.id;
    } catch {
      return null;
    }
  }, [activeConversationId, createMutation, organizationId]);

  const startNewConversation = useCallback(() => {
    // Lazy-create: don't hit the API yet. The next handleSend will lift
    // the conversation into existence. This avoids a row of empty
    // 'New chat' conversations every time the user opens the drawer.
    // Also drop any cached message pages for the previous conversation
    // — staleTime: Infinity otherwise lets the old feed flash back on
    // the next render.
    if (organizationId && activeConversationId) {
      queryClient.removeQueries({
        queryKey: CHAT_KEYS.messages(organizationId, activeConversationId),
      });
    }
    setActiveConversationId(null);
    setActiveProspectSessionRef(undefined);
    resetStream();
  }, [activeConversationId, organizationId, queryClient, resetStream]);

  const handleSelectConversation = useCallback(
    (id: string) => {
      // Same cache hygiene as startNewConversation: invalidate so the
      // selected conversation refetches fresh server state instead of
      // serving cached optimistic writes from a previous session.
      if (organizationId) {
        queryClient.invalidateQueries({
          queryKey: CHAT_KEYS.messages(organizationId, id),
        });
      }
      setActiveConversationId(id);
      setActiveProspectSessionRef(undefined);
      resetStream();
    },
    [organizationId, queryClient, resetStream],
  );

  const activeConversation = useMemo(
    () => conversations?.find((c) => c.id === activeConversationId) ?? null,
    [conversations, activeConversationId],
  );

  // Mirrors the server-side prune (deleteFromLastUserMessage): drops the last
  // user turn and everything after it from the local cache so an edited
  // message replaces it instead of stacking on the mistake.
  const pruneCacheFromLastUser = useCallback(
    (conversationId: string) => {
      if (!organizationId) return;
      queryClient.setQueryData<GetMessagesResponse>(
        CHAT_KEYS.messages(organizationId, conversationId),
        (prev) => {
          if (!prev) return prev;
          const lastUserIdx = prev.messages.map((m) => m.role).lastIndexOf('user');
          if (lastUserIdx < 0) return prev;
          return { ...prev, messages: prev.messages.slice(0, lastUserIdx) };
        },
      );
    },
    [organizationId, queryClient],
  );

  const handleSend = useCallback(
    async (content: string) => {
      const convId = await ensureConversation();
      if (!convId) return;

      if (isEditingLast) {
        setIsEditingLast(false);
        setComposerDraft(null);
        pruneCacheFromLastUser(convId);
        appendToCache(convId, { role: 'user', content, createdAt: new Date().toISOString() });
        resetStream();
        await editAndResendOrchestratorMessage(content, convId, visibleProspectSessionRef);
        return;
      }

      // Optimistic write — render the user message instantly. The backend
      // will persist it inside the orchestrator run; on next refetch we'll
      // overwrite with the canonical row (same content + role, just a
      // different id), so the optimistic entry briefly duplicates with the
      // real one and then resolves once the cache invalidates.
      appendToCache(convId, {
        role: 'user',
        content,
        createdAt: new Date().toISOString(),
      });
      setActiveProspectSessionRef(undefined);
      resetStream();
      // Pass the freshly-resolved convId explicitly so the very first
      // message in a new conversation doesn't race the parent re-render
      // — useChatStream's prop still closes over the previous null value
      // until React commits setActiveConversationId.
      await sendOrchestratorMessage(content, convId, visibleProspectSessionRef);
    },
    [
      ensureConversation,
      isEditingLast,
      pruneCacheFromLastUser,
      appendToCache,
      resetStream,
      editAndResendOrchestratorMessage,
      sendOrchestratorMessage,
      visibleProspectSessionRef,
    ],
  );

  const messages = useMemo<ProspectSearchMessage[]>(() => {
    // No active conversation = empty feed. Tanstack keeps the previous
    // query's data cached even after we disable the hook (staleTime
    // Infinity), so we have to gate the render here too — otherwise the
    // 'New chat' affordance leaves the previous chat's bubbles on screen.
    if (!activeConversationId) {
      if (isStreaming && currentText) {
        return [
          {
            role: 'assistant',
            content: currentText,
            metadata: { streaming: true },
            createdAt: new Date().toISOString(),
          },
        ];
      }
      return [];
    }

    // Defensive isolation: only render rows that actually belong to the active
    // conversation. Tanstack keeps the previous query cached (staleTime
    // Infinity), and a mis-keyed or slow-to-settle read during a "New chat"
    // transition can otherwise surface the previous conversation's feed
    // (FLOSEAI-162). Every ConversationMessage carries its conversationId.
    const list: ProspectSearchMessage[] = (serverMessages?.messages ?? [])
      .filter((m) => m.conversationId === activeConversationId)
      .map((m) => ({
        role: m.role === 'system' ? 'assistant' : m.role,
        content: m.content,
        metadata: (m.metadata ?? {}) as Record<string, unknown>,
        createdAt: m.createdAt,
        toolCalls: m.toolCalls,
      }));

    const subagentMessages = visibleProspectSessionRef ? (prospectMessages?.messages ?? []) : [];
    if (subagentMessages.length > 0) {
      list.push(...subagentMessages);
    }

    if (isStreaming && currentText) {
      list.push({
        role: 'assistant',
        content: currentText,
        metadata: { streaming: true },
        createdAt: new Date().toISOString(),
      });
    }

    // Backend returns subagent messages DESC; merge + sort ASC so the
    // feed reads top-to-bottom in chronological order regardless of source.
    return list.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
  }, [
    activeConversationId,
    serverMessages?.messages,
    prospectMessages?.messages,
    visibleProspectSessionRef,
    isStreaming,
    currentText,
  ]);

  const lastUserContent = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === 'user') return messages[i].content;
    }
    return null;
  }, [messages]);

  const canEditLast =
    !isStreaming && !!lastUserContent && !!activeConversationId && !!organizationId;

  const handleEditLastMessage = useCallback(() => {
    if (!lastUserContent) return;
    setComposerDraft(lastUserContent);
    setIsEditingLast(true);
  }, [lastUserContent]);

  const companies = prospectSession?.companies ?? [];
  const totalResults = prospectSession?.totalResults ?? 0;
  const cacheStatus = prospectSession?.cacheStatus;

  // Always render the agent panel so the user sees the prospect-search
  // surface even before they trigger a search. AgentStatusPanel falls
  // back to the three default agents (LinkedIn / Web / Instagram, idle)
  // when there's no live data.

  return (
    <KeyboardAvoidingView
      style={styles.root}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      // Topbar (~56) + safe-area top accounts for the header above the
      // tabs Slot so the input lifts cleanly above the keyboard without
      // clipping the agent status row.
      keyboardVerticalOffset={Platform.OS === 'ios' ? 56 + insets.top : 0}
    >
      <Box
        flexDirection="row"
        alignItems="center"
        paddingX="xl"
        paddingY="md"
        borderBottomWidth={1}
        borderBottomColor={colors.border.subtle}
        gap="sm"
      >
        <Pressable
          onPress={() => setDrawerOpen(true)}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Open conversations drawer"
          style={styles.headerButton}
        >
          <PanelLeft size={18} color={colors.text.secondary} strokeWidth={2} />
        </Pressable>
        <Text
          variant="bodySmall"
          color={colors.text.primary}
          numberOfLines={1}
          style={styles.headerTitle}
        >
          {activeConversation?.title ?? 'New chat'}
        </Text>
        <Pressable
          onPress={startNewConversation}
          hitSlop={8}
          accessibilityRole="button"
          accessibilityLabel="Start a new conversation"
          style={styles.headerButton}
        >
          <MessageSquarePlus size={18} color={colors.text.secondary} strokeWidth={2} />
        </Pressable>
      </Box>

      <AgentStatusPanel agents={agents} compact />
      <Box flex={1}>
        <ChatMessageList
          messages={messages}
          isSearching={isStreaming}
          searchingLabel={status?.message}
          onSuggestionPress={handleSend}
          pendingAction={pendingAction}
          isResolvingPendingAction={isResolvingPendingAction}
          onApprovePendingAction={approvePendingAction}
          onRejectPendingAction={rejectPendingAction}
        />
        {companies.length > 0 && (
          <SearchResultsPanel
            companies={companies}
            totalResults={totalResults}
            cacheStatus={cacheStatus}
          />
        )}
      </Box>
      {/*
        Lifts the composer above the home indicator on iOS / nav bar on
        Android, and gives a minimum visual gutter on web/desktop where
        insets.bottom is 0. KeyboardAvoidingView already handles vertical
        offset when the keyboard is up; this padding is only for the
        resting state when the keyboard is dismissed.
      */}
      <Box style={{ paddingBottom: COMPOSER_BOTTOM_GUTTER }}>
        {streamError && (
          <Box
            paddingX="xl"
            paddingY="sm"
            backgroundColor={colors.status.error.bg}
            borderTopWidth={1}
            borderTopColor={colors.status.error.main}
          >
            <Text variant="caption" color={colors.status.error.main}>
              {streamErrorKind === 'orchestrator'
                ? t('missionControl.chat.orchestratorError')
                : redactUserFacingText(streamError)}
            </Text>
          </Box>
        )}
        {canEditLast && (
          <Pressable
            onPress={handleEditLastMessage}
            hitSlop={8}
            accessibilityRole="button"
            accessibilityLabel={t('missionControl.chat.editLast')}
            style={styles.editLastButton}
          >
            <Pencil size={13} color={colors.text.secondary} strokeWidth={2} />
            <Text variant="caption" color={colors.text.secondary}>
              {t('missionControl.chat.editLast')}
            </Text>
          </Pressable>
        )}
        <ChatInput
          onSend={handleSend}
          onCancel={cancelOrchestratorRun}
          draft={composerDraft}
          isSearching={isStreaming}
          disabled={!organizationId}
          placeholder="Ask FlowSell anything: search prospects, qualify leads, or take actions…"
        />
      </Box>

      <ConversationDrawer
        visible={drawerOpen}
        organizationId={organizationId}
        activeConversationId={activeConversationId}
        onClose={() => setDrawerOpen(false)}
        onSelect={handleSelectConversation}
        onCreate={startNewConversation}
      />
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background.primary,
  },
  headerButton: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
  },
  editLastButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    paddingHorizontal: 20,
    paddingBottom: 6,
  },
  headerTitle: {
    flex: 1,
    textAlign: 'center',
    fontWeight: '600',
  },
});
